import './IncrementalityNavBar.scss';
import cn from 'classnames';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Img } from '@Components/Img';
import { SearchField } from '@Components/SearchField/SearchField';
import { Background, Size } from '@Components/designConstants';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { searchStrings } from '@src/utils/search';
import { INC_CDN, IncPlaybookNavPagesData } from './IncrementalityPlaybookShared';

// Pages we don't want in the nav here
const HIDDEN_PAGES: string[] = [];

interface IncrementalityNavBarProps {
  activePage: string;
  flatPageKeys: string[];
  pages: IncPlaybookNavPagesData;
  setActivePageURL: (page: string) => void;
}

export const IncrementalityNavBar = ({
  activePage,
  flatPageKeys,
  pages,
  setActivePageURL
}: IncrementalityNavBarProps) => {
  const [query, setQuery] = useState('');
  const [selectedPage, setSelectedPage] = useState<string>();
  const searchId = useId();

  const filteredRoutes = useMemo(() => {
    if (!query.trim()) return undefined;
    const matches = searchStrings(flatPageKeys, query, { rank: false });
    return matches.length === flatPageKeys.length ? undefined : matches;
  }, [flatPageKeys, query]);

  useEffect(() => {
    for (const section of Object.keys(pages)) {
      for (const page of Object.keys(pages[section])) {
        const pageContent = pages[section][page];
        if (pageContent.subPages && Object.keys(pageContent.subPages).includes(activePage)) {
          setSelectedPage(page);
          return;
        }
      }
    }
  }, [activePage, pages]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const [activeNavBarItem, activeSubNavBarItem] = useMemo(() => {
    for (const section of Object.keys(pages)) {
      for (const page of Object.keys(pages[section])) {
        const pageContent = pages[section][page];
        if (page === activePage) {
          return [page, undefined];
        }
        if (pageContent.subPages && Object.keys(pageContent.subPages).includes(activePage)) {
          return [page, activePage];
        }
      }
    }
    return [undefined, undefined];
  }, [activePage, pages]);

  const createSubNavItems = useCallback(
    (subPageKeys: string[]) => (
      <>
        {subPageKeys
          .filter(subpage => !HIDDEN_PAGES.includes(subpage))
          .map(subPage => (
            <InteractiveDiv
              className={cn('sub-item', { active: activeSubNavBarItem === subPage })}
              onClick={() => setActivePageURL(subPage.replace(/ /g, ''))}
            >
              <div className='label'>{subPage}</div>
            </InteractiveDiv>
          ))}
      </>
    ),
    [activeSubNavBarItem, setActivePageURL]
  );

  const createNavBarItem = useCallback(
    (section: string, page: string) => {
      const pageContent = pages[section][page];
      return (
        <>
          <InteractiveDiv
            className={cn('item', {
              selected: selectedPage === page,
              active: activeNavBarItem === page
            })}
            onClick={() => {
              if (selectedPage === page) {
                setSelectedPage(undefined);
                setActivePageURL(page.replace(/ /g, ''));
              } else if (pageContent.subPages) {
                setSelectedPage(page);
              } else {
                setActivePageURL(page.replace(/ /g, ''));
              }
            }}
          >
            <div className='icon'>
              {pageContent.icon ?? <Img src={`${INC_CDN}/Icon_${page}.png`} />}
            </div>
            <div className='label'> {page}</div>
          </InteractiveDiv>
          {selectedPage === page &&
            pageContent.subPages &&
            createSubNavItems(Object.keys(pageContent.subPages))}
        </>
      );
    },
    [activeNavBarItem, createSubNavItems, pages, selectedPage, setActivePageURL]
  );

  const filteredResults =
    filteredRoutes != null &&
    (filteredRoutes.length === 0 ? (
      <div className='sub-item'>
        <div className='label'>No search results</div>
      </div>
    ) : (
      createSubNavItems(filteredRoutes)
    ));

  const sectionKeys = Object.keys(pages);

  return (
    <div className='inc-playbook-nav-bar'>
      <div className='search-bar'>
        <SearchField
          size={Size.LG}
          id={searchId}
          background={Background.DARK}
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder='Search Playbook'
          aria-label='Search Playbook'
        />
      </div>
      <div className='divider-line-container' />
      {filteredRoutes == null
        ? sectionKeys.map(section => (
            <>
              {section !== sectionKeys[0] && (
                <div className='divider-line-container'>
                  <div className='divider-line' />
                </div>
              )}
              {section !== sectionKeys[0] && <div className='section-title'>{section}</div>}
              {Object.keys(pages[section])
                .filter(page => !HIDDEN_PAGES.includes(page))
                .map(page => createNavBarItem(section, page))}
            </>
          ))
        : filteredResults}
    </div>
  );
};
